import { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from './ui/Sheet';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { InvoiceIssuerForm } from './InvoiceIssuerForm';
import { normalizeInvoiceIssuer, pickIssuerPayload } from '../constants/invoiceIssuer';
import { buildInvoiceData, downloadInvoicePdf } from '../utils/invoiceGenerator';
import { formatSubscriptionDate } from '../utils/academicYear';

function applyIssuerToForm(issuer, setters) {
  const normalized = normalizeInvoiceIssuer(issuer);
  setters.setIssuer(normalized);
  setters.setLineRate(String(normalized.pricePerClassYear));
  setters.setLineDescription(normalized.lineItemDescription);
}

export function InvoiceSheet({
  open,
  onClose,
  head,
  labels,
  savedIssuer,
  fetchInvoiceIssuer,
  saveInvoiceIssuer,
}) {
  const [issuer, setIssuer] = useState(() => normalizeInvoiceIssuer(savedIssuer));
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [lineQty, setLineQty] = useState('');
  const [lineRate, setLineRate] = useState('');
  const [lineDescription, setLineDescription] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fetchRef = useRef(fetchInvoiceIssuer);
  fetchRef.current = fetchInvoiceIssuer;

  useEffect(() => {
    if (!open) return;

    applyIssuerToForm(savedIssuer, {
      setIssuer,
      setLineRate,
      setLineDescription,
    });

    let cancelled = false;
    (async () => {
      const loaded = await fetchRef.current();
      if (!cancelled && loaded) {
        applyIssuerToForm(loaded, {
          setIssuer,
          setLineRate,
          setLineDescription,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Fetch once per open — do not depend on savedIssuer (updates after fetch would re-trigger loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !head) return;
    const preview = buildInvoiceData(head, { issuer: normalizeInvoiceIssuer(issuer) });
    setLineQty(String(head.divisions ?? 0));
    setInvoiceDate(preview.invoiceDate);
    setPeriodStart(preview.periodStart);
    setPeriodEnd(preview.periodEnd);
  }, [open, head, issuer]);

  const preview = useMemo(() => {
    if (!head) return null;
    return buildInvoiceData(head, {
      issuer: { ...issuer, lineItemDescription: lineDescription, pricePerClassYear: lineRate },
      invoiceDate,
      periodStart,
      periodEnd,
      lineQty,
      lineRate,
      lineDescription,
    });
  }, [head, issuer, invoiceDate, periodStart, periodEnd, lineQty, lineRate, lineDescription]);

  const handleDownload = async () => {
    if (!head || !preview) return;
    const qty = parseInt(lineQty, 10) || 0;
    if (qty <= 0) return;
    setDownloading(true);
    try {
      const payload = pickIssuerPayload({
        ...issuer,
        lineItemDescription: lineDescription,
        pricePerClassYear: lineRate,
      });
      if (saveAsDefault) {
        await saveInvoiceIssuer(payload);
      }
      await downloadInvoicePdf({
        ...preview,
        issuer: normalizeInvoiceIssuer({
          ...issuer,
          lineItemDescription: lineDescription,
          pricePerClassYear: lineRate,
        }),
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!head) return null;

  const qty = parseInt(lineQty, 10) || 0;

  return (
    <Sheet open={open} onClose={onClose} title={labels.generateInvoice}>
      <div className="space-y-5">
        <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm">
          <p className="font-medium text-text">{head.name}</p>
          <p className="text-muted">@{head.username}</p>
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-text">{labels.invoiceIssuerSection}</p>
          <InvoiceIssuerForm labels={labels} issuer={issuer} onChange={setIssuer} compact />
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm font-semibold text-text">{labels.invoiceLineItem}</p>
          <Input label={labels.invoiceLineDescription} value={lineDescription} onChange={(e) => setLineDescription(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={labels.numDivisionsLabel}
              type="number"
              min={0}
              value={lineQty}
              onChange={(e) => setLineQty(e.target.value)}
            />
            <Input
              label={labels.invoiceRatePerClass}
              type="number"
              min={1}
              value={lineRate}
              onChange={(e) => setLineRate(e.target.value)}
            />
          </div>
          {preview && (
            <p className="text-sm font-semibold text-primary">
              {labels.invoiceTotal}: Rs. {preview.total.toLocaleString('en-IN')}
            </p>
          )}
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm font-semibold text-text">{labels.invoicePeriod}</p>
          <Input label={labels.invoiceDate} type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={labels.invoicePeriodStart} type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            <Input label={labels.invoicePeriodEnd} type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </div>
          {preview && (
            <p className="text-xs text-muted">
              {formatSubscriptionDate(preview.periodStart)} – {formatSubscriptionDate(preview.periodEnd)}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-text">
          <input
            type="checkbox"
            checked={saveAsDefault}
            onChange={(e) => setSaveAsDefault(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          {labels.invoiceSaveAsDefault}
        </label>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {labels.cancel}
          </Button>
          <Button
            className="flex-1"
            onClick={handleDownload}
            disabled={qty <= 0}
            isLoading={downloading}
          >
            {labels.invoiceDownloadPdf}
          </Button>
        </div>
        {qty <= 0 && <p className="text-xs text-danger">{labels.invoiceNoDivisions}</p>}
      </div>
    </Sheet>
  );
}
